/**
 * Administration of paired hardware service installations.
 *
 * Both endpoints are ADMIN-only on the backend: the list maps workstations to
 * shops, which is estate topology rather than anything an operator needs.
 */

/** A paired workstation, as listed for administrators. */
export interface HardwareInstallation {
    installationId: string;
    cashPointId: number;
    cashPointName: string | null;
    pairedAt: string;
    revokedAt: string | null;
}

/** Whether an installation is still able to print. */
export function isInstallationActive(installation: HardwareInstallation): boolean {
    return installation.revokedAt === null;
}

/**
 * List every paired installation, newest first.
 *
 * Returns an empty list rather than throwing, so a transient failure leaves the
 * screen empty instead of breaking the settings page around it.
 */
export async function listHardwareInstallations(
    apiBaseUrl: string,
    authHeaders: Record<string, string>,
): Promise<HardwareInstallation[]> {
    try {
        const response = await fetch(`${apiBaseUrl}/hardware-service/installations`, {
            headers: authHeaders,
        });

        if (!response.ok) {
            return [];
        }

        const body = (await response.json()) as { data?: HardwareInstallation[] };
        return body?.data ?? [];
    } catch {
        return [];
    }
}

/**
 * Withdraw an installation's binding.
 *
 * @returns `true` when the installation is now revoked.
 */
export async function revokeHardwareInstallation(
    apiBaseUrl: string,
    authHeaders: Record<string, string>,
    installationId: string,
): Promise<boolean> {
    try {
        const response = await fetch(
            `${apiBaseUrl}/hardware-service/installations/` +
                `${encodeURIComponent(installationId)}/revoke`,
            { method: 'POST', headers: authHeaders },
        );

        return response.ok;
    } catch {
        return false;
    }
}
